/**
 * Bitmap font rendering for the UI layer (doc 17 §1).
 *
 * Four faces, two authored designs. HEADER and DISPLAY are integer scales of
 * BODY, which is the whole reason they land exactly on the pixel grid at every
 * UI scale — a 1.5x face would resample and is therefore not expressible here.
 *
 * Digits are tabular in every face: a column of numbers always lines up, which
 * is what makes stat rows readable (doc 17 §8).
 */

import { PixelSurface, type Color } from '../surface.ts';
import { BODY_GLYPHS, MICRO_GLYPHS, type GlyphMap } from './glyphs.ts';

export type FaceId = 'micro' | 'body' | 'header' | 'display';

export interface FaceSpec {
  id: FaceId;
  glyphs: GlyphMap;
  /** Integer upscale of the authored design. */
  scale: number;
  /** Widen every ink pixel by 1 px after scaling — the display weight. */
  bold: boolean;
  /** Authored ink-box height in rows (cap rows + descender rows). */
  boxRows: number;
  /** Authored cap height in rows. */
  capRows: number;
  /** Line box height in UI px (doc 17 §1). */
  lineHeight: number;
  /** Gap between glyphs in UI px. */
  tracking: number;
  /** Micro has no lowercase: render it as small caps rather than tofu. */
  smallCaps: boolean;
}

export const FACES: Record<FaceId, FaceSpec> = {
  micro: {
    id: 'micro',
    glyphs: MICRO_GLYPHS,
    scale: 1,
    bold: false,
    boxRows: 5,
    capRows: 5,
    lineHeight: 7,
    tracking: 1,
    smallCaps: true,
  },
  body: {
    id: 'body',
    glyphs: BODY_GLYPHS,
    scale: 1,
    bold: false,
    boxRows: 9,
    capRows: 7,
    lineHeight: 11,
    tracking: 1,
    smallCaps: false,
  },
  header: {
    id: 'header',
    glyphs: BODY_GLYPHS,
    scale: 2,
    bold: false,
    boxRows: 9,
    capRows: 7,
    lineHeight: 20,
    tracking: 2,
    smallCaps: false,
  },
  display: {
    id: 'display',
    glyphs: BODY_GLYPHS,
    scale: 3,
    bold: true,
    boxRows: 9,
    capRows: 7,
    lineHeight: 32,
    tracking: 3,
    smallCaps: false,
  },
};

export interface TextOptions {
  /** Extra px between glyphs, on top of the face's tracking. */
  letterSpacing?: number;
  /** 1 px offset drop shadow, for text over busy world pixels. */
  shadow?: Color;
  /** Force every glyph to the tabular advance (digit-aligned tables). */
  monospace?: boolean;
  /** Uppercase the string before drawing (headers, banners). */
  caps?: boolean;
}

const DIGITS = '0123456789';

export class BitmapFont {
  readonly spec: FaceSpec;
  /** Ink-box height in UI px. */
  readonly boxHeight: number;
  /** Cap height in UI px — what optical alignment should use. */
  readonly capHeight: number;
  readonly lineHeight: number;
  private readonly digitAdvance: number;

  constructor(spec: FaceSpec) {
    this.spec = spec;
    this.boxHeight = spec.boxRows * spec.scale;
    this.capHeight = spec.capRows * spec.scale;
    this.lineHeight = spec.lineHeight;
    let widest = 1;
    for (const d of DIGITS) {
      const g = spec.glyphs[d];
      if (g) widest = Math.max(widest, g[0]?.length ?? 1);
    }
    this.digitAdvance = widest * spec.scale + this.inkBonus() + spec.tracking;
  }

  private inkBonus(): number {
    return this.spec.bold ? 1 : 0;
  }

  private lookup(ch: string): string[] | undefined {
    const g = this.spec.glyphs[ch];
    if (g) return g;
    if (this.spec.smallCaps) {
      const up = this.spec.glyphs[ch.toUpperCase()];
      if (up) return up;
    }
    return undefined;
  }

  private advanceOf(ch: string, opts: TextOptions): number {
    const extra = opts.letterSpacing ?? 0;
    if (DIGITS.includes(ch) || opts.monospace) return this.digitAdvance + extra;
    const g = this.lookup(ch);
    const w = g ? (g[0]?.length ?? 1) : 4;
    return w * this.spec.scale + this.inkBonus() + this.spec.tracking + extra;
  }

  measure(text: string, opts: TextOptions = {}): number {
    const s = opts.caps ? text.toUpperCase() : text;
    let w = 0;
    for (const ch of s) w += this.advanceOf(ch, opts);
    return Math.max(0, w - this.spec.tracking - (opts.letterSpacing ?? 0));
  }

  /**
   * Draw one line. `(x, y)` is the top-left of the ink box; the return value is
   * the pen x after the last glyph, so runs of differently-coloured text
   * concatenate exactly.
   */
  draw(
    s: PixelSurface,
    text: string,
    x: number,
    y: number,
    color: Color,
    opts: TextOptions = {},
  ): number {
    if (!Number.isInteger(x) || !Number.isInteger(y)) {
      throw new Error(`L4 Snap Law violation: text origin (${x}, ${y}) is not integer`);
    }
    const str = opts.caps ? text.toUpperCase() : text;
    let pen = x;
    for (const ch of str) {
      const adv = this.advanceOf(ch, opts);
      const g = this.lookup(ch);
      if (g) {
        // Digits are centred inside their tabular slot so columns look even.
        const gw = (g[0]?.length ?? 1) * this.spec.scale + this.inkBonus();
        const slot = DIGITS.includes(ch) || opts.monospace ? adv - (opts.letterSpacing ?? 0) - this.spec.tracking : gw;
        const gx = pen + ((slot - gw) >> 1);
        if (opts.shadow !== undefined) this.blitGlyph(s, g, gx + 1, y + 1, opts.shadow);
        this.blitGlyph(s, g, gx, y, color);
      } else if (ch !== ' ') {
        // Tofu: a hollow box, so a missing glyph is loud rather than invisible.
        const w = 3 * this.spec.scale;
        s.strokeRect(pen, y, w, this.capHeight, color);
      }
      pen += adv;
    }
    return pen;
  }

  private blitGlyph(s: PixelSurface, rows: string[], x: number, y: number, color: Color): void {
    const k = this.spec.scale;
    const w = k + this.inkBonus();
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r]!;
      for (let c = 0; c < row.length; c++) {
        if (row[c] !== '#') continue;
        s.fillRect(x + c * k, y + r * k, w, k, color);
      }
    }
  }

  /** Greedy word wrap. Returns lines that each fit `maxWidth`. */
  wrap(text: string, maxWidth: number, opts: TextOptions = {}): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (this.measure(candidate, opts) <= maxWidth || !line) {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
    return lines;
  }
}

const cache = new Map<FaceId, BitmapFont>();

export function font(id: FaceId): BitmapFont {
  let f = cache.get(id);
  if (!f) {
    f = new BitmapFont(FACES[id]);
    cache.set(id, f);
  }
  return f;
}

/** Convenience: draw `text` centred on `cx`. */
export function drawCentered(
  s: PixelSurface,
  f: BitmapFont,
  text: string,
  cx: number,
  y: number,
  color: Color,
  opts: TextOptions = {},
): void {
  const w = f.measure(text, opts);
  f.draw(s, text, cx - (w >> 1), y, color, opts);
}

/** Convenience: draw `text` right-aligned to `rx` (stat values, doc 17 §8). */
export function drawRight(
  s: PixelSurface,
  f: BitmapFont,
  text: string,
  rx: number,
  y: number,
  color: Color,
  opts: TextOptions = {},
): void {
  const w = f.measure(text, opts);
  f.draw(s, text, rx - w, y, color, opts);
}
